import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  MenuBook,
  Visibility,
  People,
  RecordVoiceOver,
  Gavel,
  Description,
  Receipt,
  Info,
  Assignment,
  AccountBalance,
  AccountBalanceWallet,
  Build,
  LocalHospital,
  School,
  SupervisorAccount,
} from "@material-ui/icons";
import {
  EasyNavigationCard,
  EasyNavigationContainer,
  EasyNavigationGroup,
  EasyNavigationGroupLabel,
} from "./easyNavigation.styles";
import { Section, SectionTitle } from "components/section";
import { useAppState } from "main/context/app-context";

export const EasyNavigationSection = () => {
  const { applicationData } = useAppState();
  const navigate = useNavigate();

  const isExternalUrl = (url: string): boolean => {
    return url.startsWith("http://") || url.startsWith("https://");
  };

  const handleNavigation = (route: string) => {
    if (isExternalUrl(route)) {
      window.open(route, "_blank");
    } else {
      navigate(route);
    }
  };

  const generalItems = useMemo(() => {
    const items = [];
    const serviceUrls = applicationData?.entity?.serviceUrls;

    if (serviceUrls?.urlDiarioOficial) {
      items.push({
        label: "DIÁRIO OFICIAL",
        Icon: MenuBook,
        route: serviceUrls.urlDiarioOficial,
      });
    } else {
      items.push({
        label: "DIÁRIO OFICIAL",
        Icon: MenuBook,
        route: "/publicacoes/diario-oficial",
      });
    }

    if (serviceUrls?.urlTransparencia) {
      items.push({
        label: "TRANSPARÊNCIA",
        Icon: Visibility,
        route: serviceUrls.urlTransparencia,
      });
    }

    if (serviceUrls?.urlServidores) {
      items.push({
        label: "SERVIDORES",
        Icon: People,
        route: serviceUrls.urlServidores,
      });
    }

    if (serviceUrls?.urlOuvidoria) {
      items.push({
        label: "OUVIDORIA",
        Icon: RecordVoiceOver,
        route: serviceUrls.urlOuvidoria,
      });
    }

    if (serviceUrls?.urlNotaFiscal) {
      items.push({
        label: "NOTA FISCAL",
        Icon: Receipt,
        route: serviceUrls.urlNotaFiscal,
      });
    }

    if (serviceUrls?.urlSic) {
      items.push({
        label: "E-SIC",
        Icon: Info,
        route: serviceUrls.urlSic,
      });
    }

    return items;
  }, [applicationData?.entity?.serviceUrls]);

  // Páginas de transparência (PNTP): sempre em sua própria linha, abaixo dos itens gerais acima.
  const transparencyItems = useMemo(() => {
    const serviceUrls = applicationData?.entity?.serviceUrls;

    return [
      { label: "LICITAÇÕES", Icon: Gavel, route: serviceUrls?.urlLicitacoes || "/licitacoes" },
      { label: "CONVÊNIOS", Icon: Assignment, route: "/convenios" },
      { label: "EMENDAS PARLAMENTARES", Icon: AccountBalance, route: "/emendas-parlamentares" },
      { label: "CONTRATOS", Icon: Description, route: serviceUrls?.urlContratos || "/contratos" },
      { label: "OBRAS", Icon: Build, route: "/obras" },
      { label: "PRESTAÇÃO DE CONTAS", Icon: AccountBalanceWallet, route: "/prestacao-contas" },
      { label: "SAÚDE", Icon: LocalHospital, route: "/documentos-setoriais/area/saude" },
      { label: "EDUCAÇÃO", Icon: School, route: "/documentos-setoriais/area/educacao" },
      { label: "ASSISTÊNCIA SOCIAL", Icon: SupervisorAccount, route: "/documentos-setoriais/area/assistencia-social" },
    ];
  }, [applicationData?.entity?.serviceUrls]);

  if (generalItems.length === 0 && transparencyItems.length === 0) {
    return null;
  }

  const renderCards = (items: typeof generalItems) =>
    items.map((item) => {
      const IconComponent = item.Icon;
      return (
        <EasyNavigationCard
          onClick={() => handleNavigation(item.route)}
          key={item.label}
        >
          <IconComponent style={{ fontSize: 48, color: 'inherit' }} />
          <span>{item.label}</span>
        </EasyNavigationCard>
      );
    });

  return (
    <Section background="gray">
      <SectionTitle>Acesso rápido</SectionTitle>
      {generalItems.length > 0 && (
        <EasyNavigationGroup>
          <EasyNavigationContainer>
            {renderCards(generalItems)}
          </EasyNavigationContainer>
        </EasyNavigationGroup>
      )}
      <EasyNavigationGroup>
        <EasyNavigationGroupLabel>Transparência Pública</EasyNavigationGroupLabel>
        <EasyNavigationContainer>
          {renderCards(transparencyItems)}
        </EasyNavigationContainer>
      </EasyNavigationGroup>
    </Section>
  );
};
